import i18nInstance from 'i18n/i18nInstance'
import { paths } from '@mc/paths'
import { NavigationEvents } from '@mc/track-event'
import { trackBrowseSkillsCatalogCTAClick } from 'components/Navigation/enterprise/utils'
import {
  trackNavClick,
  trackPlansClick,
  trackGiftCTAClick,
} from 'components/Navigation/events'
import { type Product } from './components/AccordionMenu'

export const getProducts = ({
  pathname,
  query,
  newForBusinessPageEnabled,
  Callout,
  onShowCertificatesSection,
  productType,
  onCallLinksEnabled = false,
}: {
  pathname: string
  query: Record<string, string>
  newForBusinessPageEnabled: boolean
  Callout?: React.ReactNode
  onShowCertificatesSection?: () => void
  productType: 'consumer' | 'enterprise' | 'certificates'
  onCallLinksEnabled?: boolean
}): Product[] => {
  const consumerLinks = [
    {
      title: i18nInstance.t('nav.cta.plans', {
        ns: '@mc/next',
      }),
      href: paths.checkout,
      onClick: trackPlansClick(NavigationEvents.LEVELS.TERTIARY),
    },
    {
      title: i18nInstance.t('nav.cta.gift', {
        ns: '@mc/next',
      }),
      href: paths.gift,
      onClick: () => trackGiftCTAClick(NavigationEvents.LEVELS.TERTIARY),
    },
    {
      title: i18nInstance.t('nav.cta.support', {
        ns: '@mc/next',
      }),
      href: paths.helpCenter,
      onClick: trackNavClick('Support', NavigationEvents.LEVELS.TERTIARY),
    },
    {
      title: i18nInstance.t('nav.cta.login', {
        ns: '@mc/next',
      }),
      href: paths.logIn(),
      onClick: trackNavClick('Log In', NavigationEvents.LEVELS.TERTIARY),
    },
  ]

  const certificateLinks = [
    {
      title: i18nInstance.t('nav.cta.browseAllCertificates', {
        ns: '@mc/next',
      }),
      href: productType === 'certificates' ? undefined : paths.certificates,
      onClick:
        productType === 'certificates'
          ? onShowCertificatesSection
          : trackNavClick('All Certificates', NavigationEvents.LEVELS.TERTIARY),
    },
    {
      title: i18nInstance.t('nav.cta.login', {
        ns: '@mc/next',
      }),
      href: paths.logIn(),
      onClick: trackNavClick('Log In', NavigationEvents.LEVELS.TERTIARY),
    },
  ]

  const enterpriseLinks = [
    ...(newForBusinessPageEnabled
      ? [
          {
            title: i18nInstance.t('nav.cta.howItWorks', {
              ns: '@mc/next',
            }),
            href: paths.enterpriseHowItWorks,
            onClick: trackNavClick(
              'How it works',
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
        ]
      : []),
    {
      title: i18nInstance.t('nav.cta.browseSkillsLibrary', {
        ns: '@mc/next',
      }),
      href: paths.enterpriseSkillsLibrary,
      onClick: trackBrowseSkillsCatalogCTAClick({
        action: i18nInstance.t('nav.cta.browseSkillsLibrary', {
          ns: '@mc/next',
        }),
        level: NavigationEvents.LEVELS.TERTIARY,
        pathname,
        query,
      }),
    },
    {
      title: i18nInstance.t('nav.cta.support', {
        ns: '@mc/next',
      }),
      href: paths.helpCenter,
      onClick: trackNavClick('Support', NavigationEvents.LEVELS.TERTIARY),
    },
    {
      title: i18nInstance.t('nav.cta.login', {
        ns: '@mc/next',
      }),
      href: paths.logIn(),
      onClick: trackNavClick('Log In', NavigationEvents.LEVELS.TERTIARY),
    },
  ]

  const onCallLinks = [
    ...(onCallLinksEnabled
      ? [
          {
            title: i18nInstance.t('nav.cta.exploreOnCall', {
              ns: '@mc/next',
            }),
            href: paths.onCall,
            onClick: trackNavClick(
              'Explore On Call',
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
          {
            title: i18nInstance.t('nav.cta.login', {
              ns: '@mc/next',
            }),
            href: paths.onCallLogin,
            onClick: trackNavClick('Log In', NavigationEvents.LEVELS.TERTIARY),
          },
        ]
      : []),
  ]

  return [
    {
      title: i18nInstance.t('nav.cta.masterClass', {
        ns: '@mc/next',
      }),
      type: 'consumer',
      links: consumerLinks,
      callout: productType === 'consumer' ? Callout : undefined,
      separator: productType === 'consumer' && !!Callout,
      onClick: trackNavClick(
        'MasterClass Dropdown',
        NavigationEvents.LEVELS.SECONDARY,
      ),
    },
    {
      title: i18nInstance.t('nav.cta.certificates', {
        ns: '@mc/next',
      }),
      type: 'certificates',
      links: certificateLinks,
      callout: productType === 'certificates' ? Callout : undefined,
      separator: productType === 'certificates' && !!Callout,
      onClick: trackNavClick(
        'Certificates Dropdown',
        NavigationEvents.LEVELS.SECONDARY,
      ),
    },
    {
      title: i18nInstance.t('nav.cta.work', {
        ns: '@mc/next',
      }),
      type: 'enterprise',
      links: enterpriseLinks,
      callout: productType === 'enterprise' ? Callout : undefined,
      separator: productType === 'enterprise' && !!Callout,
      onClick: trackNavClick(
        'AtWork Dropdown',
        NavigationEvents.LEVELS.SECONDARY,
      ),
    },
    ...(onCallLinksEnabled
      ? [
          {
            title: i18nInstance.t('nav.cta.onCall', {
              ns: '@mc/next',
            }),
            type: 'onCall' as const,
            links: onCallLinks,
            onClick: trackNavClick(
              'On Call Dropdown',
              NavigationEvents.LEVELS.SECONDARY,
            ),
          },
        ]
      : []),
  ]
}
